// analytics.js — Analytics page renderer + sub-renderers.
//
// Owns the cloud-only Analytics surface: totals (blocked/acquired/
// expired), top-contested files (with HMAC-hint decryption), top
// agents leaderboard, daily/hourly bar charts. Daemon mode shows
// a "team analytics is on the coordinator" placeholder rather
// than 404 against /v1/teams/analytics.
//
// The window picker (7d / 30d / 90d) and the enterprise upgrade
// CTA both have document-level click/change listeners wired at
// module init — same pattern as team-settings.js. _analyticsWindow
// is module-local; legacy/pages can read the current value via
// the getAnalyticsWindow() export when they need it.
//
// 402 / upgrade_required → show the solo/startup upgrade gate.
// Other errors → render with "--" values in the totals chips.
//
// As of Phase J7-e this is the single canonical render path. The
// legacy bridge's renderAnalytics* + getAnalyticsWindow entries
// have been dropped; pages/analytics.js imports renderAnalyticsPage
// directly from this module.
import { $, animateValue } from "./dom.js";
import { escapeHTML } from "./util.js";
// isCloudMode import was retired v0.32.3 — the daemon-mode early-return
// branch is gone now that /v1/teams/analytics works in solo.
import { api } from "./api.js";
import { decryptEventRow } from "./decrypt-helpers.js";
import { memberName, ensureMemberState } from "./members.js";
import { navigate } from "./router.js";
import { collabDrillState } from "./page-state.js";
import { isCollabDrill } from "./collab-drill.js";
import { eventsLinkAttrs, linkToEvents, openEvents, windowLabel } from "./event-links.js";

// Module-local: the selected window for the analytics query.
// "30d" by default — matched to the Protection page's window so the
// "Collisions Prevented" figure (lease.blocked from coordination_events)
// agrees across both pages on first load. Must stay in sync with the
// `selected` option on #analytics-window in index.html. The picker
// mutates it. Legacy used to expose this via getAnalyticsWindow()
// because it was a `let` in its IIFE; nothing outside this module reads
// it now, so we don't export the getter.
let _analyticsWindow = "30d";

export async function renderAnalyticsPage() {
  const gate = $("analytics-gate");
  const content = $("analytics-content");
  if (!gate || !content) return;
  // The loading skeleton shows until the first answer, whatever it is.
  const loading = $("analytics-loading");
  const loaded = () => {
    if (loading) loading.hidden = true;
  };

  // v0.32.3: in local-daemon mode the daemon now serves
  // /v1/teams/analytics with the cloud envelope (window_days +
  // totals + top_contested_files + daily + hourly + top_agents)
  // aggregated from local events. Pre-fix this branch rendered a
  // "Team analytics live on the coordinator" placeholder and
  // returned early — works in cloud-pages-only mode, but in
  // local-daemon mode the daemon is the coordinator and the data
  // is right there.
  //
  // usesCloudShapedEndpoints() returns true in BOTH cloud-pages
  // mode AND local-daemon mode, so the fetch below runs uniformly.
  // The legacy isCloudMode-false branch (coordinator-hosted, no
  // daemon, no cloud worker) is gone — that deploy mode never
  // shipped as a customer config.

  try {
    // Fetch analytics and ensure member/people state in parallel. The
    // top-agents list resolves laptop_id -> member name via memberName(),
    // which reads state.people/state.members — populated asynchronously
    // by bootstrap. On a cold page-activate this render can win that
    // race, so without ensureMemberState() the agent rows flash the raw
    // laptop_id until the next polling tick. Running both together adds
    // no latency (ensureMemberState is a no-op once state is populated).
    const [data] = await Promise.all([
      api("/v1/teams/analytics?window=" + _analyticsWindow),
      ensureMemberState(),
    ]);

    // Tier gate. The coordinator used to return HTTP 402 for non-
    // Enterprise teams, which the old catch-block below detected.
    // It now returns 200 with `analytics: null` + `tier_required`
    // so it doesn't generate browser console noise on every solo/
    // startup user's page load. Detect that shape here and route
    // it to the upgrade gate just like the 402 path did.
    if (data && (data.tier_required || data.analytics === null)) {
      loaded();
      gate.style.display = "";
      content.style.display = "none";
      return;
    }

    // Enterprise access — show content
    loaded();
    gate.style.display = "none";
    content.style.display = "";

    // Totals
    animateValue($("analytics-blocked-count"), data.totals.blocked || 0);
    animateValue($("analytics-acquired-count"), data.totals.acquired || 0);
    animateValue($("analytics-expired-count"), data.totals.expired || 0);
    linkTotals();

    renderAnalyticsContested(data.top_contested_files || []);
    renderAnalyticsTopAgents(data.top_agents || []);
    renderAnalyticsDailyChart(data.daily || [], data.window_days);
    renderAnalyticsHourlyChart(data.hourly || []);
    renderAnalyticsCommunication(data.communication || null, data.window_days);
  } catch (e) {
    loaded();
    const msg = (e && e.message) || "";
    // Legacy 402 fallback — kept in case the coordinator regresses
    // to the old behavior or a deploy lag leaves the dashboard
    // ahead of the worker.
    if (msg.includes("402") || msg.includes("upgrade_required")) {
      gate.style.display = "";
      content.style.display = "none";
    } else {
      // Other error
      gate.style.display = "none";
      content.style.display = "";
      const blocked = $("analytics-blocked-count");
      const acquired = $("analytics-acquired-count");
      const expired = $("analytics-expired-count");
      if (blocked) blocked.textContent = "--";
      if (acquired) acquired.textContent = "--";
      if (expired) expired.textContent = "--";
      // Coordination-health tiles get the same treatment (the card
      // body may be hidden if a previous render hit the empty-state;
      // that's fine — these writes are then invisible no-ops).
      for (const id of [
        "analytics-comm-score",
        "analytics-comm-reply-rate",
        "analytics-comm-broadcasts",
        "analytics-comm-notes",
        "analytics-comm-direct",
        "analytics-comm-nudges",
      ]) {
        const el = $(id);
        if (el) el.textContent = "--";
      }
    }
  }
}

// linkTotals makes each total open the events it counts, over the window.
function linkTotals() {
  const win = windowLabel(_analyticsWindow);
  const panel = (id) => {
    const el = $(id);
    return el ? el.closest(".panel") : null;
  };
  linkToEvents(panel("analytics-blocked-count"), { types: ["lease.blocked"], since: _analyticsWindow, label: "Collisions prevented · " + win });
  linkToEvents(panel("analytics-acquired-count"), { types: ["lease.acquired"], since: _analyticsWindow, label: "Edits coordinated · " + win });
  linkToEvents(panel("analytics-expired-count"), { types: ["lease.expired"], since: _analyticsWindow, label: "Expired leases · " + win });
}

async function renderAnalyticsContested(rows) {
  const wrap = $("analytics-contested-files");
  if (!wrap) return;
  if (rows.length === 0) {
    wrap.innerHTML =
      '<div class="empty" style="padding: var(--s5);"><div class="empty-detail">No collisions in this window. Either very quiet, or your team picks disjoint files.</div></div>';
    return;
  }
  const max = Math.max(...rows.map((r) => r.blocks));
  const html = await Promise.all(
    rows.map(async (r) => {
      // Resolve a human-readable path from the row's payload_hint via the
      // canonical decryptEventRow extraction. payload_hint is the AES-GCM
      // ciphertext of the WHOLE event payload (JSON), not just the path — so
      // it must be decrypted, JSON-parsed, and the path pulled from
      // last_file_path / purpose / resource in precedence order. decryptEventRow
      // does exactly that (and falls back to the hashed wire resource when the
      // key is missing or nothing decrypts). The previous code decrypted the
      // trailing hash fragment of the hashed resource — never valid ciphertext
      // — so every cloud row showed the raw `file:h:…` hash. In solo mode the
      // resource is already plaintext and there's no payload_hint, so the
      // fallback yields the real path unchanged.
      const decrypted = await decryptEventRow({
        event_type: "lease.blocked",
        resource: r.resource,
        payload_hint: r.payload_hint || "",
      });
      const resolved = decrypted.resource || r.resource;
      // A resolved path differs from the raw hashed wire resource only when
      // decryption succeeded; use that to style + color the row (accent when
      // humanized, tertiary when still a hash).
      const isHumanized =
        resolved !== r.resource && !/:h:[0-9a-f]+/i.test(resolved);
      const label = resolved;
      const pct = Math.round((r.blocks / max) * 100);
      const link = eventsLinkAttrs({
        types: ["lease.blocked"],
        since: _analyticsWindow,
        resource: r.resource,
        label: "Collisions on " + label + " · " + windowLabel(_analyticsWindow),
      });
      return (
        '<div class="kpi-row"' + link + ' style="padding: var(--s3) var(--s4); border-bottom: 1px solid var(--border);">' +
        '<div style="display: flex; justify-content: space-between; font-size: 12.5px; margin-bottom: 4px;">' +
        '<span style="font-family: var(--font-mono); color: ' +
        (isHumanized ? "var(--accent)" : "var(--text-tertiary)") +
        '; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 70%;">' +
        escapeHTML(label) +
        "</span>" +
        '<span style="color: var(--text-secondary); font-family: var(--font-mono); font-variant-numeric: tabular-nums;">' +
        r.blocks +
        "</span>" +
        "</div>" +
        '<div style="height: 4px; background: var(--bg-elevated); border-radius: 2px; overflow: hidden;"><div style="height: 100%; width: ' +
        pct +
        '%; background: linear-gradient(90deg, var(--warning), var(--danger)); border-radius: 2px;"></div></div>' +
        "</div>"
      );
    }),
  );
  wrap.innerHTML = html.join("");
}

function renderAnalyticsTopAgents(rows) {
  const wrap = $("analytics-top-agents");
  if (!wrap) return;
  if (rows.length === 0) {
    wrap.innerHTML =
      '<div class="empty" style="padding: var(--s5);"><div class="empty-detail">No lease acquisitions in this window.</div></div>';
    return;
  }
  const max = Math.max(...rows.map((r) => r.acquires));
  wrap.innerHTML = rows
    .map((r) => {
      const tool = (r.agent_id || "").split("/")[0] || "unknown";
      const shortID = (r.agent_id || "").split("/").pop().slice(0, 8);
      const machine = memberName ? memberName(r.laptop_id) : r.laptop_id;
      const pct = Math.round((r.acquires / max) * 100);
      const link = eventsLinkAttrs({
        types: ["lease.acquired"],
        since: _analyticsWindow,
        agent: r.agent_id,
        label: "Edits coordinated by " + tool + " " + shortID + " · " + windowLabel(_analyticsWindow),
      });
      return (
        '<div class="kpi-row"' + link + ' style="padding: var(--s3) var(--s4); border-bottom: 1px solid var(--border);">' +
        '<div style="display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 4px;">' +
        '<span style="min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">' +
        '<span style="color: var(--info); font-family: var(--font-mono); font-weight: 600;">' +
        escapeHTML(tool) +
        "</span>" +
        '<span style="color: var(--text-tertiary); font-family: var(--font-mono); font-size: 11px; margin-left: 4px;">' +
        escapeHTML(shortID) +
        "</span>" +
        '<span style="color: var(--text-secondary); font-size: 12px; margin-left: 8px;">on ' +
        escapeHTML(machine || "--") +
        "</span>" +
        "</span>" +
        '<span style="color: var(--text); font-family: var(--font-mono); font-weight: 600; font-variant-numeric: tabular-nums;">' +
        r.acquires +
        "</span>" +
        "</div>" +
        '<div style="height: 4px; background: var(--bg-elevated); border-radius: 2px; overflow: hidden;"><div style="height: 100%; width: ' +
        pct +
        '%; background: var(--accent); border-radius: 2px;"></div></div>' +
        "</div>"
      );
    })
    .join("");
}

// Clicking a day's bar opens that day's edits, collisions and expired
// leases. Days are UTC dates, as the server groups them.
let _dailyGeom = null;

export function dayAt(geom, x) {
  if (!geom) return "";
  const i = Math.floor((x - geom.padX) / geom.barW);
  return i >= 0 && i < geom.days.length ? geom.days[i] : "";
}

export function daySpec(day) {
  const start = new Date(day + "T00:00:00Z");
  const end = new Date(start.getTime() + 86400000);
  const label = start.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  return {
    types: ["lease.acquired", "lease.blocked", "lease.expired"],
    since: start.toISOString(),
    until: end.toISOString(),
    label: "Edits, collisions and expired leases · " + label,
  };
}

function bindDailyChartClicks(canvas) {
  if (canvas.dataset.dayLinks) return;
  canvas.dataset.dayLinks = "1";
  canvas.style.cursor = "pointer";
  canvas.setAttribute("data-tooltip", "Click a day to see its events");
  canvas.addEventListener("click", (e) => {
    const r = canvas.getBoundingClientRect();
    const day = dayAt(_dailyGeom, e.clientX - r.left);
    if (day) openEvents(daySpec(day));
  });
}

function renderAnalyticsDailyChart(rows, windowDays) {
  const canvas = $("analytics-daily-chart");
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth,
    h = canvas.clientHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  // Build per-day bucket: { '2026-04-19': { acquired: X, blocked: Y, expired: Z } }
  const byDay = {};
  const days = [];
  const now = new Date();
  for (let i = windowDays - 1; i >= 0; i--) {
    const d = new Date(now - i * 86400000);
    const key = d.toISOString().slice(0, 10);
    byDay[key] = { acquired: 0, blocked: 0, expired: 0 };
    days.push(key);
  }
  for (const r of rows) {
    if (byDay[r.day]) {
      const type = r.event_type.replace("lease.", "");
      if (byDay[r.day][type] !== undefined) byDay[r.day][type] = r.cnt;
    }
  }

  const maxVal = Math.max(
    1,
    ...days.map((d) =>
      Math.max(byDay[d].acquired, byDay[d].blocked, byDay[d].expired),
    ),
  );
  const barW = (w - 20) / days.length;
  const colW = barW * 0.8;
  const padX = 10;
  _dailyGeom = { days, barW, padX };
  bindDailyChartClicks(canvas);

  const css = getComputedStyle(document.body);
  const colors = {
    acquired: css.getPropertyValue("--accent").trim(),
    blocked: css.getPropertyValue("--success").trim(),
    expired: css.getPropertyValue("--warning").trim(),
  };

  days.forEach((d, i) => {
    const x = padX + i * barW + (barW - colW) / 2;
    // Stack: acquired (bottom), blocked (middle), expired (top)
    let y = h - 20;
    ["acquired", "blocked", "expired"].forEach((type) => {
      const v = byDay[d][type];
      if (v === 0) return;
      const barH = (v / maxVal) * (h - 35);
      ctx.fillStyle = colors[type];
      ctx.fillRect(x, y - barH, colW, barH);
      y -= barH;
    });

    // Day label (every Nth based on windowDays)
    const step = windowDays > 30 ? 7 : windowDays > 7 ? 3 : 1;
    if (i % step === 0 || i === days.length - 1) {
      ctx.fillStyle = css.getPropertyValue("--text-tertiary").trim();
      ctx.font = "10px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(d.slice(5), x + colW / 2, h - 4);
    }
  });
}

// renderAnalyticsCommunication — the "Collaboration" card (v0.67
// redesign). Reads the analytics envelope's `communication` object:
// {direct_sent, broadcast_sent, replies, notes_added, lease_nudges,
// reply_rate, score, collab_delta_pct, nag_delta_pct, daily:
// [{date, direct, broadcast, replies, notes, nudges}], top_agents}.
// Both the cloud worker and the solo daemon emit the same shape; a
// coordinator that predates the score/delta fields simply yields a
// low score and 0 deltas (all fields default to 0), and a coordinator
// that predates the whole object lands in the empty-state.
//
// The card tells ONE story: agents working together. Hero score dial +
// verdict, a signal row of four sparkline-backed behaviors, and a
// subordinate receding "nag noise" lane on its own scale. Collision
// prevention is deliberately absent — it lives on the Protection tab.
//
// _lastComm is retained module-side so the theme-toggle / resize
// redraw (drawCommCharts) can repaint the canvases with the fresh
// CSS-var colors without a re-fetch.
let _lastComm = null;
let _lastCommWindow = 30;

function renderAnalyticsCommunication(comm, windowDays) {
  const empty = $("analytics-comm-empty");
  const body = $("analytics-comm-body");
  if (!empty || !body) return;
  _lastCommWindow = windowDays;
  const total = comm
    ? (comm.direct_sent || 0) +
      (comm.broadcast_sent || 0) +
      (comm.replies || 0) +
      (comm.notes_added || 0) +
      (comm.lease_nudges || 0)
    : 0;
  if (!comm || total === 0) {
    empty.style.display = "";
    body.style.display = "none";
    _lastComm = null;
    return;
  }
  empty.style.display = "none";
  body.style.display = "";
  _lastComm = comm;

  // Window label in the header.
  const win = $("analytics-comm-window");
  if (win) win.textContent = "last " + windowDays + " days";

  // HERO — score dial. animateValue tweens the number; the arc is set
  // to the same 0..100 fraction. Dial circumference = 2πr, r=58 → ~364.
  const score = Math.max(0, Math.min(100, Math.round(comm.score || 0)));
  animateValue($("analytics-comm-score"), score);
  const arc = $("analytics-comm-dial-arc");
  if (arc) {
    const CIRC = 364;
    arc.style.transition = "stroke-dashoffset .8s cubic-bezier(.22,1,.36,1)";
    arc.setAttribute("stroke-dashoffset", String(CIRC - (score / 100) * CIRC));
  }

  // HERO — verdict eyebrow is the collaboration trend delta.
  const delta = Math.round(comm.collab_delta_pct || 0);
  const vk = $("analytics-comm-verdict-k");
  if (vk) {
    const up = delta >= 0;
    const arrow = up ? "↑ up " : "↓ down ";
    vk.innerHTML =
      '<span style="color: var(--' +
      (up ? "success" : "danger") +
      ')">' +
      arrow +
      Math.abs(delta) +
      "%</span> this period";
  }

  // SIGNAL ROW — four behaviors. Reply rate is a %, the rest are counts.
  const rate = $("analytics-comm-reply-rate");
  // Clamp defensively: pre-fix coordinators can return >1 (450% bug).
  if (rate)
    rate.textContent = String(
      Math.min(100, Math.round((comm.reply_rate || 0) * 100)),
    );
  animateValue($("analytics-comm-broadcasts"), comm.broadcast_sent || 0);
  animateValue($("analytics-comm-notes"), comm.notes_added || 0);
  animateValue($("analytics-comm-direct"), comm.direct_sent || 0);

  // NAG — subordinate lane. Value + falling-trend chip.
  animateValue($("analytics-comm-nudges"), comm.lease_nudges || 0);
  const nagTrend = $("analytics-comm-nag-trend");
  if (nagTrend) {
    const nd = Math.round(comm.nag_delta_pct || 0);
    // Falling nags are healthy → green down-arrow. Rising → danger up.
    const down = nd <= 0;
    // Server caps magnitude at ±500 (a tiny prev window explodes the
    // ratio); render the cap as "500%+" so it reads as off-the-scale,
    // not a precise figure.
    const mag = Math.abs(nd);
    const magText = mag >= 500 ? "500%+" : mag + "%";
    nagTrend.textContent = (down ? "↓ " : "↑ ") + magText + " vs last period";
    nagTrend.style.color = down ? "var(--success)" : "var(--danger)";
  }

  drawCommCharts();
}

// Each Collaboration number (and "View messages") carries data-collab-kind
// and opens the Coordination page filtered to the items it counts, over the
// window the card is showing (collab-drill.js).
function openCollabList(el) {
  const drill = { kind: el.dataset.collabKind, days: _lastCommWindow };
  if (!isCollabDrill(drill)) return;
  collabDrillState.drill = drill;
  collabDrillState.page = 0;
  collabDrillState.loadedKey = "";
  navigate("coordination");
}
document.addEventListener("click", (e) => {
  const el = e.target.closest("[data-collab-kind]");
  if (!el) return;
  e.preventDefault();
  openCollabList(el);
});
document.addEventListener("keydown", (e) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  const el = e.target.closest("[data-collab-kind]");
  if (!el) return;
  e.preventDefault();
  openCollabList(el);
});

// smoothCommPath — monotone-cubic control-point smoothing for the
// sparklines (clean curves that never overshoot the data).
function smoothCommPath(ctx, pts) {
  if (pts.length < 2) {
    if (pts.length === 1) ctx.moveTo(pts[0][0], pts[0][1]);
    return;
  }
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i > 0 ? i - 1 : 0];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2 < pts.length ? i + 2 : i + 1];
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2[0], p2[1]);
  }
}

// setupCommCanvas — the shared dpr-scaled canvas idiom (matches
// renderAnalyticsDailyChart), returning a cleared 2D context sized in
// CSS pixels.
function setupCommCanvas(cv) {
  const ctx = cv.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const w = cv.clientWidth,
    h = cv.clientHeight;
  cv.width = w * dpr;
  cv.height = h * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  return { ctx, w, h };
}

// denseCommDaily — expand the sparse wire `daily` series into a dense,
// windowDays-long array of per-day values for `series`, filling gaps
// with 0 (same densify pattern the lease charts use).
function denseCommDaily(rows, windowDays, series) {
  const byDay = {};
  const order = [];
  const now = new Date();
  for (let i = windowDays - 1; i >= 0; i--) {
    const key = new Date(now - i * 86400000).toISOString().slice(0, 10);
    byDay[key] = 0;
    order.push(key);
  }
  for (const r of rows || []) {
    if (byDay[r.date] === undefined) continue;
    byDay[r.date] = r[series] || 0;
  }
  return order.map((k) => byDay[k]);
}

// denseCommDates — the ordered ISO day keys (oldest→newest) for a
// windowDays-long window, matching the index order denseCommDaily
// returns values in. Used to label hover tooltips with the right date.
function denseCommDates(windowDays) {
  const order = [];
  const now = new Date();
  for (let i = windowDays - 1; i >= 0; i--) {
    order.push(new Date(now - i * 86400000).toISOString().slice(0, 10));
  }
  return order;
}

// fmtCommDay — "2026-07-15" → "Jul 15" for tooltips. Parsed as UTC
// (the keys are UTC ISO dates) so the label matches the bucket day
// regardless of the viewer's timezone.
function fmtCommDay(iso) {
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

// _commTip — drive the dashboard's existing body-level #ac-tooltip
// element for canvas hover. The widgets.js tooltip only fires on
// [data-tooltip] mouseover of HTML elements, so it can't read a point
// inside a <canvas>; rather than build a second tooltip system we
// position that SAME element ourselves. show() sets the text + the
// .visible class and clamps into the viewport (mirrors positionTooltip
// in widgets.js); hide() clears .visible.
const _commTip = {
  el() {
    return document.getElementById("ac-tooltip");
  },
  show(text, clientX, clientY) {
    const t = this.el();
    if (!t) return;
    t.textContent = text;
    t.classList.add("visible");
    const tw = t.offsetWidth;
    const th = t.offsetHeight;
    let top = clientY - th - 12;
    let left = clientX - tw / 2;
    if (top < 4) top = clientY + 16;
    if (left < 4) left = 4;
    if (left + tw > window.innerWidth - 4) left = window.innerWidth - tw - 4;
    t.style.top = top + "px";
    t.style.left = left + "px";
  },
  hide() {
    const t = this.el();
    if (t) t.classList.remove("visible");
  },
};

// attachCommHover — wire per-point hover tooltips on a comm canvas.
// `points` is [{x, date, value}] in CSS-pixel x-coords matching what
// was drawn; `label` builds the tooltip string (e.g. "3 broadcasts").
// Nearest-point-by-x lookup so it agrees with the drawn geometry. The
// handler is (re)installed on every draw — the canvas holds only the
// latest closure, and the idempotent flag avoids stacking listeners
// across repaints (theme-toggle / resize / re-fetch).
function attachCommHover(cv, points, label) {
  cv._commHoverPoints = points;
  cv._commHoverLabel = label;
  if (cv._commHoverWired) return;
  cv._commHoverWired = true;
  cv.style.cursor = "crosshair";
  cv.addEventListener("pointermove", (e) => {
    const pts = cv._commHoverPoints;
    if (!pts || pts.length === 0) return;
    const rect = cv.getBoundingClientRect();
    const x = e.clientX - rect.left;
    let best = pts[0];
    let bestD = Infinity;
    for (const p of pts) {
      const d = Math.abs(p.x - x);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    _commTip.show(
      fmtCommDay(best.date) + ": " + cv._commHoverLabel(best.value),
      e.clientX,
      e.clientY,
    );
  });
  cv.addEventListener("pointerleave", () => _commTip.hide());
}

// commSparkline — one signal-cell sparkline: smooth monotone curve over
// REAL daily data, ~12% area-fill wash, 2px round-cap line, endpoint
// dot with a 2px surface ring. Normalizes to the series' own min/max so
// low-volume signals still show shape.
function commSparkline(cv, color, data, dates, unit) {
  const s = setupCommCanvas(cv);
  const ctx = s.ctx,
    w = s.w,
    h = s.h,
    pad = 5;
  const css = getComputedStyle(document.body);
  const surface = css.getPropertyValue("--bg-panel-solid").trim() || "#fff";
  // Baseline.
  ctx.strokeStyle = css.getPropertyValue("--border").trim();
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, h - pad + 0.5);
  ctx.lineTo(w, h - pad + 0.5);
  ctx.stroke();

  const n = data.length;
  if (n === 0) return;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  // Flat series (all equal) sit at 45% height so they're not glued to
  // an edge; otherwise scale into [0.08, 0.92] of the inner band.
  const norm =
    max === min
      ? () => 0.45
      : (v) => 0.08 + 0.84 * ((v - min) / span);
  const pts =
    n === 1
      ? [
          [pad, h - pad - (h - 2 * pad) * norm(data[0])],
          [w - pad, h - pad - (h - 2 * pad) * norm(data[0])],
        ]
      : data.map((d, i) => [
          pad + ((w - 2 * pad) * i) / (n - 1),
          h - pad - (h - 2 * pad) * norm(d),
        ]);

  // Area wash (~12%).
  ctx.beginPath();
  smoothCommPath(ctx, pts);
  ctx.lineTo(pts[pts.length - 1][0], h - pad);
  ctx.lineTo(pts[0][0], h - pad);
  ctx.closePath();
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, hexA(color, 0.18));
  g.addColorStop(1, hexA(color, 0));
  ctx.fillStyle = g;
  ctx.fill();

  // Line.
  ctx.beginPath();
  smoothCommPath(ctx, pts);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.stroke();

  // Endpoint dot + surface ring.
  const e = pts[pts.length - 1];
  ctx.beginPath();
  ctx.arc(e[0], e[1], 4, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = surface;
  ctx.stroke();

  // Hover tooltips: one point per real data day. x uses the SAME
  // formula the multi-point line uses (single-point series draws a flat
  // line but still tooltips its one day across the whole width). unit is
  // [singular, plural] → "1 reply" / "3 replies".
  const hoverPts = data.map((v, i) => ({
    x: n === 1 ? w / 2 : pad + ((w - 2 * pad) * i) / (n - 1),
    date: (dates && dates[i]) || "",
    value: v,
  }));
  attachCommHover(cv, hoverPts, (v) => v + " " + (v === 1 ? unit[0] : unit[1]));
}

// commNagChart — the subordinate receding nag lane: wide faint coral
// area on its OWN scale (nags are 10–100x message volume and trend
// down). Real daily `nudges` series.
function commNagChart(cv, rows, windowDays) {
  const s = setupCommCanvas(cv);
  const ctx = s.ctx,
    w = s.w,
    h = s.h,
    pad = 4;
  // Reserve a bottom band for the dated x-axis (this is the only comm
  // chart that gets one — the small tile sparklines stay axis-free by
  // design). The curve draws in [pad, baseY]; labels sit below baseY.
  const axisH = 14;
  const baseY = h - axisH;
  const css = getComputedStyle(document.body);
  const col = css.getPropertyValue("--danger").trim();
  const muted = css.getPropertyValue("--text-tertiary").trim();
  const surface = css.getPropertyValue("--bg-panel-solid").trim() || "#fff";
  const data = denseCommDaily(rows, windowDays, "nudges");
  const dates = denseCommDates(windowDays);
  const n = data.length;
  if (n === 0) return;
  const max = Math.max(1, ...data);
  const xAt = (i) => (n === 1 ? w / 2 : pad + ((w - 2 * pad) * i) / (n - 1));
  const pts = data.map((d, i) => [
    xAt(i),
    pad + (baseY - 2 * pad) - (baseY - 2 * pad) * (d / max),
  ]);
  ctx.beginPath();
  smoothCommPath(ctx, pts);
  ctx.lineTo(pts[n - 1][0], baseY);
  ctx.lineTo(pts[0][0], baseY);
  ctx.closePath();
  const g = ctx.createLinearGradient(0, 0, 0, baseY);
  g.addColorStop(0, hexA(col, 0.15));
  g.addColorStop(1, hexA(col, 0));
  ctx.fillStyle = g;
  ctx.fill();
  ctx.beginPath();
  smoothCommPath(ctx, pts);
  ctx.strokeStyle = col;
  ctx.lineWidth = 1.75;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.globalAlpha = 0.85;
  ctx.stroke();
  ctx.globalAlpha = 1;
  const e = pts[n - 1];
  ctx.beginPath();
  ctx.arc(e[0], e[1], 3.5, 0, Math.PI * 2);
  ctx.fillStyle = col;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = surface;
  ctx.stroke();

  // Dated x-axis — hairline baseline + a few muted day labels, matching
  // the Daily Activity chart's idiom exactly (same MM-DD date slice,
  // same every-Nth-label step, same --text-tertiary color / font).
  ctx.strokeStyle = css.getPropertyValue("--border").trim();
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, baseY + 0.5);
  ctx.lineTo(w, baseY + 0.5);
  ctx.stroke();
  const step = windowDays > 30 ? 7 : windowDays > 7 ? 3 : 1;
  ctx.fillStyle = muted;
  ctx.font = "10px Inter, sans-serif";
  dates.forEach((d, i) => {
    if (i % step !== 0 && i !== n - 1) return;
    ctx.textAlign = i === 0 ? "left" : i === n - 1 ? "right" : "center";
    ctx.fillText(d.slice(5), xAt(i), h - 3);
  });

  // Hover tooltips over the nag series (its own nudges values).
  const hoverPts = data.map((v, i) => ({
    x: xAt(i),
    date: dates[i] || "",
    value: v,
  }));
  attachCommHover(cv, hoverPts, (v) => v + " nag" + (v === 1 ? "" : "s"));
}

// hexA — apply an alpha to a CSS color. Handles #rgb / #rrggbb (the
// token palette) and falls back to a color-mix() for anything else
// (e.g. a light-theme var() indirection), so the wash never breaks.
function hexA(color, alpha) {
  const c = (color || "").trim();
  let m = /^#([0-9a-f]{6})$/i.exec(c);
  if (m) {
    const int = parseInt(m[1], 16);
    return (
      "rgba(" +
      ((int >> 16) & 255) +
      "," +
      ((int >> 8) & 255) +
      "," +
      (int & 255) +
      "," +
      alpha +
      ")"
    );
  }
  m = /^#([0-9a-f]{3})$/i.exec(c);
  if (m) {
    const r = parseInt(m[1][0] + m[1][0], 16);
    const g = parseInt(m[1][1] + m[1][1], 16);
    const b = parseInt(m[1][2] + m[1][2], 16);
    return "rgba(" + r + "," + g + "," + b + "," + alpha + ")";
  }
  return "color-mix(in srgb, " + c + " " + Math.round(alpha * 100) + "%, transparent)";
}

// drawCommCharts — (re)paint the four signal sparklines + the nag lane
// from _lastComm. Called on render and on theme-toggle / resize so the
// canvases pick up fresh CSS-var colors. No-op when the card is empty.
function drawCommCharts() {
  if (!_lastComm) return;
  const comm = _lastComm,
    windowDays = _lastCommWindow;
  const css = getComputedStyle(document.body);
  const colorFor = {
    replies: css.getPropertyValue("--success").trim(),
    broadcast: css.getPropertyValue("--info").trim(),
    notes: css.getPropertyValue("--warning").trim(),
    direct: css.getPropertyValue("--accent").trim(),
  };
  // Tooltip unit per series as [singular, plural] ("1 reply" / "3 replies").
  const unitFor = {
    replies: ["reply", "replies"],
    broadcast: ["broadcast", "broadcasts"],
    notes: ["note", "notes"],
    direct: ["direct message", "direct messages"],
  };
  const dates = denseCommDates(windowDays);
  document.querySelectorAll(".collab-spark").forEach((cv) => {
    const kind = cv.dataset.series;
    const color = colorFor[kind];
    if (!color) return;
    commSparkline(
      cv,
      color,
      denseCommDaily(comm.daily || [], windowDays, kind),
      dates,
      unitFor[kind] || [kind, kind],
    );
  });
  const nag = $("analytics-comm-nag-chart");
  if (nag) commNagChart(nag, comm.daily || [], windowDays);
}

function renderAnalyticsHourlyChart(rows) {
  const canvas = $("analytics-hourly-chart");
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth,
    h = canvas.clientHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  const byHour = Array(24).fill(0);
  for (const r of rows) {
    if (r.hour >= 0 && r.hour < 24) byHour[r.hour] = r.cnt;
  }
  const maxVal = Math.max(1, ...byHour);

  const barW = (w - 20) / 24;
  const colW = barW * 0.7;
  const padX = 10;
  const css = getComputedStyle(document.body);
  // accentColor was computed but never used in the legacy version —
  // the bar fill uses an explicit rgba(96, 165, 250, …) instead.
  // Keeping the parity drop the unused var.

  byHour.forEach((v, hr) => {
    const x = padX + hr * barW + (barW - colW) / 2;
    const barH = (v / maxVal) * (h - 35);
    // gradient intensity based on value
    const alpha = 0.3 + 0.7 * (v / maxVal);
    ctx.fillStyle = "rgba(96, 165, 250, " + alpha + ")";
    ctx.fillRect(x, h - 20 - barH, colW, barH);

    // Label every 6 hours
    if (hr % 6 === 0 || hr === 23) {
      ctx.fillStyle = css.getPropertyValue("--text-tertiary").trim();
      ctx.font = "10px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(String(hr).padStart(2, "0") + ":00", x + colW / 2, h - 4);
    }
  });
}

// ---- module init: wire window picker + upgrade button ----
//
// Both elements live in the static HTML scaffold, so wiring on
// document-level event delegation at module init time matches
// legacy's behavior.
//
// `window.track` is set by dashboard-init.js (telemetry). It
// loads before the bundle so is always defined at runtime; the
// `typeof` guard is defensive against the missing-script
// configuration that solo daemon mode uses.
//
// startCheckout still lives in bootstrap.js. Look it up
// via the AC_LEGACY bridge so a load-order race doesn't matter
// — by the time a user can click "Upgrade", legacy has long
// since populated AC_LEGACY.
function wireListeners() {
  document.addEventListener("change", (e) => {
    if (e.target.id === "analytics-window") {
      _analyticsWindow = e.target.value;
      renderAnalyticsPage();
      if (typeof window.track === "function") {
        window.track("analytics.window_changed", {
          window: _analyticsWindow,
        });
      }
    }
  });
  document.addEventListener("click", (e) => {
    if (e.target.id === "analytics-upgrade-btn") {
      if (typeof window.track === "function") {
        window.track("analytics.upgrade_clicked");
      }
      const checkout = window.AC_LEGACY && window.AC_LEGACY.startCheckout;
      if (typeof checkout === "function") checkout("enterprise");
    }
  });

  // Collaboration-card canvases read CSS-var colors at draw time, so a
  // theme flip or a resize must repaint them (they don't re-fetch).
  // drawCommCharts is a no-op when the card is empty / off-screen.
  new MutationObserver(() => drawCommCharts()).observe(
    document.documentElement,
    { attributes: true, attributeFilter: ["data-theme"] },
  );
  let _commResizeTimer = null;
  window.addEventListener("resize", () => {
    clearTimeout(_commResizeTimer);
    _commResizeTimer = setTimeout(drawCommCharts, 120);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
